import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { LogoComponent } from '../../../shared/logo/logo.component';
import { ThemeToggleComponent } from '../../../shared/theme-toggle/theme-toggle.component';

type Method = 'microsoft' | 'google' | 'email';

const METHOD_LABEL: Record<Method, string> = {
  microsoft: 'Conectando con Microsoft…',
  google: 'Conectando con Google…',
  email: 'Enviando el enlace…',
};

/** Suficiente para atajar el dedo resbalado; el rebote real lo dicta el correo. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'lc-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ThemeToggleComponent, LogoComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private readonly auth = inject(AuthService);

  /**
   * Qué método está en curso, no un booleano: el indicador de carga sale solo
   * en el control que el usuario pulsó. Con un `busy` plano, los dos botones de
   * proveedor girarían y no habría forma de saber cuál se está abriendo.
   */
  protected readonly pending = signal<Method | null>(null);
  protected readonly error = signal<string | null>(null);

  protected readonly email = signal('');

  /**
   * A qué dirección salió el enlace, no un booleano de "enviado": el aviso
   * repite el correo tecleado, que es donde se descubre la errata antes de
   * quedarse esperando un mensaje que nunca llega.
   */
  protected readonly linkSentTo = signal<string | null>(null);

  protected readonly busy = computed(() => this.pending() !== null);

  /**
   * El redirect a OAuth tarda lo suficiente como para que el spinner sin voz
   * deje fuera a quien navega por lector de pantalla.
   */
  protected readonly pendingLabel = computed(() => {
    const method = this.pending();
    return method ? METHOD_LABEL[method] : null;
  });

  protected microsoft(): Promise<void> {
    return this.run('microsoft', () => this.auth.signInWithMicrosoft());
  }

  protected google(): Promise<void> {
    return this.run('google', () => this.auth.signInWithGoogle());
  }

  protected onEmailInput(event: Event): void {
    this.email.set((event.target as HTMLInputElement).value);
    // El aviso de enviado habla de la dirección anterior. En cuanto se toca el
    // campo deja de ser cierto, así que se retira antes de que confunda.
    this.linkSentTo.set(null);
  }

  protected async sendLink(event: Event): Promise<void> {
    event.preventDefault();

    const address = this.email().trim();
    if (!EMAIL.test(address)) {
      this.error.set('Escribe un correo válido para enviarte el enlace.');
      return;
    }

    this.pending.set('email');
    this.error.set(null);
    this.linkSentTo.set(null);

    try {
      await this.auth.signInWithEmailLink(address);
      this.linkSentTo.set(address);
    } catch {
      this.error.set('No pudimos enviar el enlace. Inténtalo de nuevo.');
    } finally {
      // Al contrario que en OAuth, aquí el usuario se queda en la página: si el
      // botón no vuelve de su estado de carga, parece colgado.
      this.pending.set(null);
    }
  }

  private async run(method: Method, action: () => Promise<void>): Promise<void> {
    this.pending.set(method);
    this.error.set(null);
    try {
      await action();
      // Sin `finally`: en el camino feliz el navegador ya va camino del
      // proveedor y devolver los botones a su estado normal produce un
      // parpadeo justo antes de que la página desaparezca.
    } catch {
      this.error.set('No pudimos iniciar sesión. Inténtalo de nuevo.');
      this.pending.set(null);
    }
  }
}
